<template>
  <div>
    <el-upload
      class="video-upload"
      list-type="text"
      :limit="limit"
      :class="hasFileUpload && 'upload-success'"
      :action="qiniuUploadUrl"
      :data="qiniuData"
      :before-upload="handleBeforeUpload"
      :file-list="fileList"
      :on-success="handleUploadSuccess"
      :on-error="handleUploadError"
      :on-remove="handleFileRemove">
      <el-button v-if="showUpload" size="small" type="primary">{{ $t('microSite.component.upload.clickToUploadVideo') }}</el-button>
      <div v-if="showUpload" class="el-upload__tip" slot="tip">
        <div>{{ $t('microSite.component.upload.videoLimitTips', {videoMaxSize}) }}</div>
        <div v-if="otherTips">{{ otherTips }}</div>
      </div>
    </el-upload>
    <el-upload
      v-if="!showUpload"
      ref="imageUploader"
      class="image-upload"
      list-type="picture"
      :limit="1"
      :class="hasImageUpload && 'upload-success'"
      :action="qiniuUploadUrl"
      :data="qiniuData"
      :before-upload="handleImageBeforeUpload"
      :file-list="imageFileList"
      :on-success="handleImageUploadSuccess"
      :on-error="handleImageUploadError"
      :on-remove="handleImageFileRemove"
      >
      <el-button v-if="showImageUpload" size="small" type="primary">{{ $t('microSite.component.upload.clickToUploadVideoCover') }}</el-button>
      <div v-if="showImageUpload" class="el-upload__tip" slot="tip">{{ this.$t('microSite.component.upload.imgLimitTips', { imageMaxSize: this.imageMaxSize }) }}</div>
    </el-upload>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ACCEPT_FILE_TYPE } from '@/constant/crm'

export default {
  model: {
    prop: 'video',
    event: 'change'
  },
  props: {
    video: {
      required: true
    },
    otherTips: {
      required: false
    }
  },
  data () {
    return {
      qiniuUploadUrl: window.location.protocol + '//upload.qiniup.com',
      qiniuData: {
        key: null,
        token: null
      },
      videoMaxSize: 100,
      imageMaxSize: 10,
      videoUrl: null,
      imageUrl: null,
      hasFileUpload: false,
      hasImageUpload: false,
      limit: 1,
      fileList: [],
      imageFileList: [],
      uploadPrefix: 'microsite/video/'
    }
  },
  computed: {
    showUpload () {
      const videoUrl = this.video.src
      return !videoUrl || !this.hasFileUpload ||
      (this.videoUrl && (this.videoUrl.indexOf(this.uploadPrefix) === -1))
    },
    showImageUpload () {
      const imageUrl = this.video.coverImage
      return !imageUrl || !this.hasImageUpload ||
      (this.imageUrl && (this.imageUrl.indexOf(this.uploadPrefix) === -1))
    }
  },
  async created () {
    this.videoUrl = this.video.src
    this.imageUrl = this.video.coverImage
    this.getFileList(this.video)
    const resp = await this.getQiniuUploadToken({ is_public: true })
    this.qiniuData.token = resp.token
  },
  methods: {
    ...mapActions('common', ['getQiniuUploadToken']),
    ...mapGetters('account', ['getCurrentAccount']),
    handleBeforeUpload (file) {
      const isMP4 = file.type === 'video/mp4'
      const isLt100M = file.size / 1024 / 1024 <= this.videoMaxSize
      if (!isMP4) {
        this.$message.error(this.$t('microSite.component.upload.videoTypeError'))
        return false
      }
      if (!isLt100M) {
        this.$message.error(this.$t('microSite.component.upload.videoSizeError', { videoMaxSize: this.videoMaxSize }))
        return false
      }
      this.qiniuData.key = this.fileKeyGenerator(file)

      return true
    },
    handleUploadSuccess (resp, file, fileList) {
      this.hasFileUpload = true
      const url = `${process.env.VUE_APP_QINIU_DOMAIN}${this.qiniuData.key}`
      this.videoUrl = url
    },
    handleUploadError (resp, file, fileList) {
      this.hasFileUpload = false
      this.videoUrl = ''
      this.$message({
        message: this.$t('microSite.component.upload.uploadFailed'),
        duration: 2000,
        type: 'warning'
      })
    },
    handleFileRemove (file, fileList) {
      this.hasFileUpload = false
      this.videoUrl = ''
      this.hasImageUpload = false
      this.imageUrl = ''
    },
    handleImageBeforeUpload (file) {
      if (!ACCEPT_FILE_TYPE.includes(file.type)) {
        this.$message.error(this.$t('microSite.component.upload.imgTypeError'))
        return false
      }
      if (file.size > this.imageMaxSize * 1024 * 1024) {
        this.$message.error(this.$t('microSite.component.upload.imgSizeError', { imageMaxSize: this.imageMaxSize }))
        return false
      }
      this.qiniuData.key = this.fileKeyGenerator(file, true)
      return true
    },
    handleImageUploadSuccess (resp, file, fileList) {
      this.hasImageUpload = true
      const url = `${process.env.VUE_APP_QINIU_DOMAIN}${this.qiniuData.key}`
      this.imageUrl = url
    },
    handleImageUploadError (resp, file, fileList) {
      this.hasImageUpload = false
      this.imageUrl = ''
      this.$message({
        message: this.$t('microSite.component.upload.uploadFailed'),
        duration: 2000,
        type: 'warning'
      })
    },
    handleImageFileRemove (file, fileList) {
      this.hasImageUpload = false
      this.imageUrl = ''
    },
    fileKeyGenerator (file, isCover) {
      let prefix = this.uploadPrefix + this.getCurrentAccount().id + '/'
      if (isCover) {
        prefix += 'cover/'
      }
      return prefix + new Date().valueOf()
    },
    getFileList (video) {
      if (video) {
        if (video.src && video.src.indexOf(this.uploadPrefix) > -1) {
          this.fileList = [{ name: this.$t('microSite.component.upload.videoDuplicate'), url: video.src }]
        } else {
          this.fileList = []
        }
        if (video.coverImage && video.coverImage.indexOf(this.uploadPrefix) > -1) {
          this.imageFileList = [{ name: '', url: video.coverImage }]
        } else {
          this.imageFileList = []
        }
      }
      this.hasFileUpload = this.fileList.length > 0
      this.hasImageUpload = this.imageFileList.length > 0
    }
  },
  watch: {
    videoUrl (newVal) {
      if (newVal === this.video.src) {
        return
      }
      this.$emit('change', {
        ...this.video,
        src: newVal
      })
      this.getFileList({
        ...this.video,
        src: newVal
      })
    },
    imageUrl (newVal) {
      if (newVal === this.video.coverImage) {
        return
      }
      this.$emit('change', {
        ...this.video,
        coverImage: newVal
      })
      this.getFileList({
        ...this.video,
        coverImage: newVal
      })
    }
  }
}
</script>

<style lang="scss">
.video-upload {
  &.upload-success {
    .el-upload {
      display: block;
      text-align: left;
    }
  }

  .el-upload__tip {
    line-height: initial;
    word-break: break-word;

    div {
      margin: 5px 0;
    }
  }

}
</style>
